import React, { useState, useContext, useEffect } from 'react';
import { Container, Col, Card, CardHeader, CardBody } from 'reactstrap';
import classNames from 'classnames';
import styles from './SalesConfirmation.module.css';
import BrandedIcon from '../_BrandingProvider/BrandedIcon';
import { BrandingContext } from '../contexts/ContractContext';
import getClientContent from '../_BrandingProvider/brandContent.jsx';
import { stripPhoneNumber } from '../shared/util';
import { useHistory, useLocation } from 'react-router';
import useLoadAtTop from '../_ReactHooks/useLoadAtTop';
import { dataLayerPush, getCustomerRedirectUrls } from '../../helpers';
import { usePageTitle } from '../_ReactHooks';
import { useParams } from 'react-router-dom';

// HELPERS
const getMetaData = (confirmationId) => {
    switch (confirmationId) {
        case '1':
            return {
                browserTab: 'Contact Confirmation',
                dataLayer: {
                    pageTitle: 'NJR Retail Service Plans – Contact Confirmation',
                    pagePath: 'confirmation/contact'
                }
            };
        case '2':
            return {
                browserTab: 'Save Confirmation',
                dataLayer: {
                    pageTitle: 'NJR Retail Service Plans – Save Confirmation',
                    pagePath: 'confirmation/save'
                }
            };
        case '3':
            return {
                browserTab: 'Sale Confirmation',
                dataLayer: {
                    pageTitle: 'NJR Retail Service Plans – Sale Confirmation',
                    pagePath: 'confirmation/sale'
                }
            };
        default:
            return;
    }
};

// COMPONENT
const SalesConfirmation = () => {
    /**
     *  ===============================
     *  =========== HOOKS =============
     *  ===============================
     */
    useLoadAtTop();
    const { confirmationId } = useParams();
    usePageTitle(getMetaData(confirmationId)?.browserTab);
    const history = useHistory();
    const location = useLocation();
    const branding = useContext(BrandingContext);
    const clientId = branding.clientId || 'AIG';
    const clientLower = clientId?.toLowerCase();

    /**
     *  ===============================
     *  =========== STATE =============
     *  ===============================
     */
    const [yearlyPlan] = useState(location.state?.isYearly ?? false);
    const [coverageDocsLink, setCoverageDocsLink] = useState('');
    const selectedPlan = location.state?.plan || '';
    const isSinglePlan = selectedPlan === 'Single Device Plan (2-year)' ? true : false;

    /**
     *  ===============================
     *  ========== EFFECT =============
     *  ===============================
     */
    useEffect(() => {
        window.onpopstate = function (event) {
            event.preventDefault();
            if (location.pathname.toLowerCase().includes('confirmation')) {
                history.push('/');
            }
        };

        dataLayerPush('Pageview', getMetaData(confirmationId)?.dataLayer);
    }, []);

    useEffect(() => {
        getCoverageDocsLink();
    }, [clientLower]);

    /**
     *  ================================
     *  === CONTINUE COMPONENT LOGIC ===
     *  ================================
     */

    const customerServicePhone = getClientContent(clientId)?.variableText?.claim?.phone;

    const getCoverageDocsLink = async () => {
        const data = await getCustomerRedirectUrls(clientLower).then((urlResponse) => {
            return {
                coverageURL: urlResponse?.coverageDocsUrl
            };
        });

        setCoverageDocsLink(data?.coverageURL);
    };

    const viewCoverageDocs = () => {
        window.open(coverageDocsLink, '_blank');
    };

    return (
        <Container className={styles.confirmContainer}>
            {/* CONTACT CONFIRMATION */}
            {confirmationId === '1' && (
                <div className={styles.ContactConfirmation}>
                    <span className={styles.contactIcon}>
                        <BrandedIcon iconType='contactus' />
                    </span>
                    <h1 className='h3'>We’ll Contact You Soon</h1>
                    <h2 className='h6'>We will contact you within 1 business day about your request.</h2>
                    <p>
                        If you’d like to speak with one of our representatives, call{' '}
                        <a
                            href={stripPhoneNumber(customerServicePhone)}
                            className='callLink'
                        >
                            {customerServicePhone}
                        </a>
                        .
                    </p>
                </div>
            )}

            {/* SAVED CONFIRMATION */}
            {confirmationId === '2' && (
                <Card className={styles.onlineConfirmCard}>
                    <CardHeader className={styles.cardheader}>
                        <h1 className='h3'>Your quote has been saved.</h1>
                    </CardHeader>
                    <CardBody className={styles.confirmCardBody}>
                        <BrandedIcon iconType={'success'} />
                        <h3 className='h4'>Invitation Number: {location.state.invitationNumber}</h3>
                        <Col
                            md='10'
                            className={styles.confirmMessage}
                        >
                            <h2 className={classNames('h3', styles.njrCustomH2)}>
                                You will receive an email with a link&nbsp;
                                <br className='show-lg-up' />
                                to resume your purchase.
                            </h2>
                            <p>
                                If you&apos;d like to speak with one of our representatives, call &nbsp;
                                <a
                                    href={`tel:${stripPhoneNumber(customerServicePhone)}`}
                                    className='callLink pl-0'
                                >
                                    {customerServicePhone}
                                </a>
                                <br />
                                Please reference your Invitation Number: {location.state.invitationNumber}.
                            </p>
                        </Col>
                    </CardBody>
                </Card>
            )}

            {/* SALES CONFIRMATION */}
            {confirmationId === '3' && (
                <Card className={styles.onlineConfirmCard}>
                    <CardHeader className={styles.cardheader}>
                        <h1 className='h3'>Thank you for your payment of ${location.state.amount ?? '0.00'}.</h1>
                    </CardHeader>
                    <CardBody className={styles.confirmCardBody}>
                        <BrandedIcon iconType={'success'} />
                        <h2>Your plan purchase was successful!</h2>
                        <p className={styles.planNum}>Plan Number: {location.state.invitationNumber}</p>

                        <Col
                            md='12'
                            className={styles.alignMessage}
                        >
                            <p className='mb-3'>
                                Thank you for your {location.state.riskProfileName} purchase! You will receive an email
                                confirmation within 24 hours. We will send your coverage documents by email within 7-14
                                business days or by mail within 30 days.
                            </p>
                            {!yearlyPlan && (
                                <p className='mb-3'>
                                    Payments of ${location.state.amount ?? '0.00'} will be automatically charged to the{' '}
                                    {location.state.paymentType === 'cardPayment' ? 'card' : 'bank account'} provided
                                    every thirty (30) days. The charge will appear on your billing statement as "NJR
                                    Retail".
                                </p>
                            )}
                            <p className='mb-3'>
                                For questions about your plan or to file a claim call &nbsp;
                                <a
                                    href={`tel:${stripPhoneNumber(customerServicePhone)}`}
                                    className='callLink pl-0'
                                >
                                    {customerServicePhone}
                                </a>
                                .
                            </p>
                        </Col>
                        <button
                            className='btnLink mb-2'
                            onClick={window.print}
                        >
                            Print this page for your records
                        </button>
                    </CardBody>
                </Card>
            )}

            {/* APPLE UNBUNDLED SALES CONFIRMATION */}
            {confirmationId === '4' && (
                <div className={styles.contentContainer}>
                    <BrandedIcon iconType='success' />
                    <h1 className='h3'>Thank you for your payment of ${location.state?.amount ?? '0.00'}.</h1>
                    <p className={styles.planNum}>Plan Number: {location.state?.planNumber}</p>
                    <Col
                        md='10'
                        className={styles.alignContent}
                    >
                        <p className='mb-3'>
                            You should receive an email confirmation within 24 hours and your plan documents by email
                            within 3-5 days. Please keep your plan number in a safe place.
                        </p>
                        <p className='mb-3'>
                            Your AIG Standalone Theft and Loss Protection Plan covers a maximum of{' '}
                            {isSinglePlan ? 'two (2) replacements for a single device' : 'three (3) total replacements'}{' '}
                            within a 12-month period. Covered device must be listed on your plan at the time of the
                            incident. Each claim is subject to a deductible, deductible amount varies by device.
                        </p>
                        <p className='mb-3'>
                            For questions about your plan or to file a claim call
                            <a
                                href={`tel:${stripPhoneNumber(customerServicePhone)}`}
                                className='callLink ml-1'
                            >
                                {customerServicePhone}
                            </a>
                            .
                        </p>
                    </Col>
                    <button
                        className='btnLink mb-5'
                        onClick={viewCoverageDocs}
                    >
                        View Coverage Documents
                    </button>
                </div>
            )}
        </Container>
    );
};
export default SalesConfirmation;
